import { trpc, trpcClient } from '@/lib/trpc';
import { Integration } from '@prisma/client';
import { FC, useState } from 'react';
import { queryClient } from '@/lib/trpc';
import { IntegrationFeature, IntegrationProvider, ConfigModalProps } from '../types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog';
import { Button } from '@deepguide-ai/ui/button';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { createOAuthConnectLifecycle, openOAuthConnectPopup } from '../oauth-connect-lifecycle';

/**
 * Google Drive icon wrapper component
 */
const GoogleDriveIconWrapper: FC = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9.5 4L4.5 14L7.86 20H19.14L22.5 14L17.5 4H9.5ZM10.5 6H16.5L20 13.5H17L13.5 6H10.5ZM9 6.5L12.5 14H5L9 6.5ZM14.5 15L16 18H10L11.5 15H14.5Z"
      fill="#4285F4"
    />
  </svg>
);

/**
 * Google Drive config modal component
 */
const GoogleDriveConfigModal: FC<ConfigModalProps> = ({
  open,
  onOpenChange,
  workspaceId,
  onComplete,
}) => {
  const [isConnecting, setIsConnecting] = useState(false);

  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      await GoogleDriveProvider.handleConnect({
        workspaceId,
        onSuccess: () => {
          onComplete();
        },
      });
    } catch (error) {
      console.error('Failed to connect to Google Drive:', error);
      setIsConnecting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Connect to Google Drive</DialogTitle>
          <DialogDescription>
            Connect your Google Drive account to import documents, spreadsheets, and presentations into your workspace.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 p-4">
          <div className="text-sm text-muted-foreground">
            You'll be redirected to Google to authorize the connection. Make sure pop-ups are enabled for this site.
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isConnecting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConnect}
              disabled={isConnecting}
            >
              {isConnecting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Connecting...
                </>
              ) : (
                'Connect to Google Drive'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Google Drive integration provider
 */
export const GoogleDriveProvider: IntegrationProvider = {
  id: 'GoogleDrive',
  name: 'Google Drive',
  description: 'Connect to Google Drive to import files and documents',
  Icon: GoogleDriveIconWrapper,
  ConfigModal: GoogleDriveConfigModal,

  getFeatures: (): IntegrationFeature[] => [
    {
      id: 'files',
      name: 'Files',
      description: 'Import files from Google Drive',
      enabled: true,
    },
    {
      id: 'docs',
      name: 'Documents',
      description: 'Import Google Docs, Sheets, and Slides',
      enabled: true,
    },
  ],

  // Handler to connect to Google Drive
  handleConnect: async ({ workspaceId, features, redirectUrl, onSuccess, onFinish }) => {
    const { finish } = createOAuthConnectLifecycle(onFinish)
    try {
      const data = await trpcClient.integrations.initGoogleDriveAuth.mutate({
        workspaceId,
        features,
        // Use the central callback handler with provider parameter
        redirectUrl: `${window.location.origin}/auth-callback/GoogleDrive`,
        providerType: 'GoogleDrive',
      });

      if (!data.authUrl) {
        console.error('No auth URL returned from initialization');
        finish('error');
        return;
      }

      // Open auth page in a popup window
      openOAuthConnectPopup({
        authUrl: data.authUrl, windowName: 'google-drive-auth', provider: 'GoogleDrive',
        onSuccess: result => {
          void queryClient.invalidateQueries({ queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey() });
          onSuccess?.(result);
        },
        onError: message => toast.error(message),
        onPopupBlocked: () => toast.error('OAuth popup was blocked. Allow popups and try again.'),
        onFinish: finish,
      })
    } catch (error) {
      console.error('Error initializing Google Drive auth:', error);
      toast.error(
        `Error initializing Google Drive authentication: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
      finish('error');
    }
  },

  onConnectComplete: async ({ code, state }: { code: string, state: string }) => {
    return await trpcClient.integrations.completeGoogleDriveAuth.mutate({ code, state });
  },

  // Sync data from Google Drive
  syncData: async (integration: Integration) => {
    try {
      // Sync the Google Drive data
      await trpcClient.integrations.syncIntegration.mutate({
        id: integration.id,
      });

      // Invalidate queries to refresh the UI
      queryClient.invalidateQueries({
        queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey(),
      });
    } catch (error) {
      console.error('Error syncing Google Drive data:', error);
    }
  },
}; 